import React, { useEffect, useState } from "react";
import { Sparkles, MessageCircleQuestion, Wrench, AlertCircle, HandCoins } from "lucide-react";
import api from "@/lib/api";
import {
  EMPTY_AI_SUPPORT_BRIEF,
  SERVICE_BOOK_OPTIONS,
  SERVICE_WHERE_OPTIONS,
  NEGOTIATION_TONE_OPTIONS,
  AI_BRIEF_FLAG_DEFS,
  normalizeAiSupportBrief,
  aiSupportBriefToPayload,
  aiSupportBriefValid,
  listingContextForAiBrief,
  aiSupportBriefCompleteness,
} from "@/lib/aiSupportBrief";

export {
  EMPTY_AI_SUPPORT_BRIEF,
  normalizeAiSupportBrief,
  aiSupportBriefToPayload,
  aiSupportBriefValid,
};

function faqAnswerFor(brief, id) {
  const row = (brief.faq || []).find((r) => r.id === id);
  return row?.answer || "";
}

function setFaqAnswer(brief, id, question, answer) {
  const faq = [...(brief.faq || [])];
  const idx = faq.findIndex((r) => r.id === id);
  const row = { id, question, answer };
  if (idx >= 0) faq[idx] = row;
  else faq.push(row);
  return { ...brief, faq };
}

function SectionTitle({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-2 text-xs font-medium text-slate-200 pt-2">
      {Icon && <Icon className="w-3.5 h-3.5 text-electric" strokeWidth={1.75} />}
      {children}
    </div>
  );
}

export function AiSupportBriefEditor({ value, onChange, car, testidPrefix = "ai-support-brief" }) {
  const v = normalizeAiSupportBrief(value || EMPTY_AI_SUPPORT_BRIEF);
  const set = (key, val) => onChange?.({ ...v, [key]: val });
  const setFlag = (key, checked) => onChange?.({ ...v, flags: { ...v.flags, [key]: checked } });
  const [suggested, setSuggested] = useState([]);
  const [suggestLoading, setSuggestLoading] = useState(true);

  const ctx = listingContextForAiBrief(car);
  const { score, max } = aiSupportBriefCompleteness(v, car);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/my/ai-support/suggested-questions");
        if (!cancelled) setSuggested(Array.isArray(r.data?.questions) ? r.data.questions : []);
      } catch {
        if (!cancelled) setSuggested([]);
      } finally {
        if (!cancelled) setSuggestLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const onFaqChange = (id, question, answer) => {
    onChange?.(setFaqAnswer(v, id, question, answer));
  };

  const unansweredSuggestions = suggested.filter((q) => !faqAnswerFor(v, q.id).trim());

  return (
    <div
      data-testid={testidPrefix}
      className="rounded-2xl border border-electric/25 bg-electric/[0.06] p-4 space-y-4 mt-3"
    >
      <div className="flex items-start gap-2">
        <Sparkles className="w-4 h-4 text-electric shrink-0 mt-0.5" strokeWidth={1.75} />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm font-medium text-white">AI asszisztens brief</div>
            <span
              data-testid={`${testidPrefix}-completeness`}
              className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-slate-400"
            >
              Készenlét {score}/{max}
            </span>
          </div>
          <p className="text-[11px] text-slate-400 mt-0.5 leading-relaxed">
            Privát infó — <b>nem jelenik meg</b> a hirdetésben. Az AI ebből <b>saját szavaival</b>, röviden válaszol; a
            chatben már szereplő adatokat (ár, km, extrák) nem ismétli feleslegesen.
          </p>
        </div>
      </div>

      {ctx.hints.length > 0 && (
        <div
          data-testid={`${testidPrefix}-listing-hints`}
          className="text-[10px] text-slate-500 rounded-xl bg-white/[0.03] border border-white/5 px-3 py-2 space-y-0.5"
        >
          <div className="text-slate-400 font-medium mb-1">Már a nyilvános hirdetésben:</div>
          {ctx.hints.map((h) => (
            <div key={h}>· {h}</div>
          ))}
        </div>
      )}

      <SectionTitle icon={null}>Megtekintés & elérhetőség</SectionTitle>
      <label className="block text-xs text-slate-400">
        Mikor érsz rá megtekintésre?
        <textarea
          data-testid={`${testidPrefix}-availability`}
          value={v.availability}
          onChange={(e) => set("availability", e.target.value)}
          rows={2}
          className="mt-1 w-full rounded-xl bg-white/[0.04] border border-white/10 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:border-electric outline-none resize-y min-h-[56px]"
          placeholder="Pl. Kedden és csütörtökön 18 után, szombaton 10–14 között…"
        />
      </label>
      <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
        <input
          type="radio"
          name={`${testidPrefix}-addr-pol`}
          checked={v.viewing_address_policy === "after_agreement"}
          onChange={() => set("viewing_address_policy", "after_agreement")}
        />
        Pontos cím csak egyeztetés után (ajánlott)
      </label>
      <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
        <input
          type="radio"
          name={`${testidPrefix}-addr-pol`}
          checked={v.viewing_address_policy === "as_listed"}
          onChange={() => set("viewing_address_policy", "as_listed")}
        />
        A hirdetésben megadott helyen (térkép / cím)
      </label>

      <SectionTitle icon={Wrench}>Szerviz & műszaki háttér</SectionTitle>
      <label className="block text-xs text-slate-400">
        Utolsó szerviz (dátum / km / mit cseréltek)
        <input
          data-testid={`${testidPrefix}-last-service`}
          type="text"
          value={v.last_service}
          onChange={(e) => set("last_service", e.target.value)}
          className="mt-1 w-full rounded-xl bg-white/[0.04] border border-white/10 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:border-electric outline-none"
          placeholder="Pl. 2025 tavasz, 85 ezer km-nél olajcsere + szűrők"
        />
      </label>
      {!ctx.skipServiceBookSelect && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <label className="block text-xs text-slate-400">
            Szervizkönyv
            <select
              data-testid={`${testidPrefix}-service-book`}
              value={v.service_book}
              onChange={(e) => set("service_book", e.target.value)}
              className="mt-1 w-full rounded-xl bg-white/[0.04] border border-white/10 px-3 py-2 text-sm text-white focus:border-electric outline-none"
            >
              {SERVICE_BOOK_OPTIONS.map((o) => (
                <option key={o.value || "x"} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-xs text-slate-400">
            Szervizelés jellemzően
            <select
              data-testid={`${testidPrefix}-service-where`}
              value={v.service_where}
              onChange={(e) => set("service_where", e.target.value)}
              className="mt-1 w-full rounded-xl bg-white/[0.04] border border-white/10 px-3 py-2 text-sm text-white focus:border-electric outline-none"
            >
              {SERVICE_WHERE_OPTIONS.map((o) => (
                <option key={o.value || "x"} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}
      <label className="block text-xs text-slate-400">
        Szervizmúlt röviden (opcionális)
        <textarea
          data-testid={`${testidPrefix}-service-history`}
          value={v.service_history}
          onChange={(e) => set("service_history", e.target.value)}
          rows={2}
          className="mt-1 w-full rounded-xl bg-white/[0.04] border border-white/10 px-3 py-2 text-sm resize-y min-h-[48px]"
          placeholder="Pl. végig márkaszervizben, nagy szerviz 80 e km…"
        />
      </label>
      <label className="block text-xs text-slate-400">
        Következő szerviz / karbantartás (opcionális)
        <input
          data-testid={`${testidPrefix}-next-service`}
          value={v.next_service}
          onChange={(e) => set("next_service", e.target.value)}
          className="mt-1 w-full rounded-xl bg-white/[0.04] border border-white/10 px-3 py-2 text-sm"
          placeholder="Pl. olajcsere ~90 e km-nél"
        />
      </label>

      <SectionTitle icon={AlertCircle}>Állapot & őszinteség</SectionTitle>
      <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
        <input
          type="checkbox"
          data-testid={`${testidPrefix}-no-known-issues`}
          checked={v.no_known_issues}
          onChange={(e) => set("no_known_issues", e.target.checked)}
          className="rounded border-white/20"
        />
        Nincs ismert rejtett hiba (az AI ezt mondhatja, ha kérdezik)
      </label>
      <label className="block text-xs text-slate-400">
        Apró esztétikai hibák (karcolás, kőfelverés…)
        <textarea
          data-testid={`${testidPrefix}-cosmetic`}
          value={v.cosmetic_issues}
          onChange={(e) => set("cosmetic_issues", e.target.value)}
          rows={2}
          disabled={v.no_known_issues}
          className="mt-1 w-full rounded-xl bg-white/[0.04] border border-white/10 px-3 py-2 text-sm disabled:opacity-40 resize-y min-h-[48px]"
          placeholder="Pl. jobb első ajtón kis parkolásnyom"
        />
      </label>
      <label className="block text-xs text-slate-400">
        Műszaki / működési megjegyzés (opcionális)
        <textarea
          data-testid={`${testidPrefix}-mechanical`}
          value={v.mechanical_issues}
          onChange={(e) => set("mechanical_issues", e.target.value)}
          rows={2}
          disabled={v.no_known_issues}
          className="mt-1 w-full rounded-xl bg-white/[0.04] border border-white/10 px-3 py-2 text-sm disabled:opacity-40 resize-y min-h-[48px]"
        />
      </label>
      <label className="block text-xs text-slate-400">
        Baleset / javított sérülés (opcionális)
        <textarea
          data-testid={`${testidPrefix}-accident`}
          value={v.accident_notes}
          onChange={(e) => set("accident_notes", e.target.value)}
          rows={2}
          className="mt-1 w-full rounded-xl bg-white/[0.04] border border-white/10 px-3 py-2 text-sm resize-y min-h-[48px]"
          placeholder="Pl. nincs baleset / 2022-ben javított lökhárító"
        />
      </label>

      <SectionTitle icon={HandCoins}>Alku & eladás</SectionTitle>
      <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
        <input
          data-testid={`${testidPrefix}-negotiable`}
          type="checkbox"
          checked={v.negotiable !== false}
          onChange={(e) => set("negotiable", e.target.checked)}
          className="rounded border-white/20"
        />
        Alkuképes az ár
      </label>
      {v.negotiable !== false && (
        <>
          <label className="block text-xs text-slate-400">
            Belső árpadló (Ft) — a vevő <b>soha nem látja</b>, csak az AI tud alkudni
            <input
              data-testid={`${testidPrefix}-price-floor`}
              type="number"
              min={0}
              value={v.price_floor_huf}
              onChange={(e) => set("price_floor_huf", e.target.value)}
              className="mt-1 w-full rounded-xl bg-white/[0.04] border border-white/10 px-3 py-2 text-sm"
              placeholder={car?.price ? `Pl. ${Math.round(car.price * 0.92)}` : "Opcionális"}
            />
          </label>
          <label className="block text-xs text-slate-400">
            Alkustílus
            <select
              data-testid={`${testidPrefix}-negotiation-tone`}
              value={v.negotiation_tone}
              onChange={(e) => set("negotiation_tone", e.target.value)}
              className="mt-1 w-full rounded-xl bg-white/[0.04] border border-white/10 px-3 py-2 text-sm"
            >
              {NEGOTIATION_TONE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        </>
      )}
      {!ctx.skipWarrantyField && (
        <label className="block text-xs text-slate-400">
          Garancia részletek (ha nincs a hirdetés extráiban)
          <input
            data-testid={`${testidPrefix}-warranty`}
            value={v.warranty_private}
            onChange={(e) => set("warranty_private", e.target.value)}
            className="mt-1 w-full rounded-xl bg-white/[0.04] border border-white/10 px-3 py-2 text-sm"
            placeholder="Pl. 6 hó kereskedői garancia"
          />
        </label>
      )}
      <label className="block text-xs text-slate-400">
        Honnan az autó / forgalom (ha nincs a leírásban)
        <input
          data-testid={`${testidPrefix}-origin`}
          value={v.origin_notes}
          onChange={(e) => set("origin_notes", e.target.value)}
          className="mt-1 w-full rounded-xl bg-white/[0.04] border border-white/10 px-3 py-2 text-sm"
        />
      </label>
      <label className="block text-xs text-slate-400">
        Miért adod el? (privát, opcionális)
        <input
          data-testid={`${testidPrefix}-why-selling`}
          value={v.why_selling}
          onChange={(e) => set("why_selling", e.target.value)}
          className="mt-1 w-full rounded-xl bg-white/[0.04] border border-white/10 px-3 py-2 text-sm"
        />
      </label>

      <div className="flex flex-wrap gap-3">
        {AI_BRIEF_FLAG_DEFS.map(({ key, label }) => (
          <label key={key} className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
            <input
              type="checkbox"
              data-testid={`${testidPrefix}-flag-${key}`}
              checked={!!v.flags?.[key]}
              onChange={(e) => setFlag(key, e.target.checked)}
              className="rounded border-white/20"
            />
            {label}
          </label>
        ))}
      </div>

      {(suggested.length > 0 || suggestLoading) && (
        <div className="pt-2 border-t border-white/10 space-y-2" data-testid={`${testidPrefix}-faq-block`}>
          <div className="flex items-start gap-2">
            <MessageCircleQuestion className="w-4 h-4 text-electric shrink-0 mt-0.5" strokeWidth={1.75} />
            <div>
              <div className="text-xs font-medium text-slate-200">Gyakori vevői kérdések</div>
              <p className="text-[10px] text-slate-500 mt-0.5 leading-relaxed">
                Rövid tényeket adj — az AI másképp fogalmaz, nem másolja be szó szerint.
              </p>
            </div>
          </div>
          {suggestLoading && <p className="text-[10px] text-slate-500 px-1">Javaslatok betöltése…</p>}
          {!suggestLoading &&
            suggested.map((q) => (
              <label key={q.id} className="block text-xs text-slate-400">
                <span className="text-slate-300">{q.question}</span>
                {q.count > 0 && (
                  <span className="text-[10px] text-slate-600 ml-1">· {q.count}× feltve</span>
                )}
                <textarea
                  data-testid={`${testidPrefix}-faq-${q.id}`}
                  value={faqAnswerFor(v, q.id)}
                  onChange={(e) => onFaqChange(q.id, q.question, e.target.value)}
                  rows={2}
                  className="mt-1 w-full rounded-xl bg-white/[0.04] border border-white/10 px-3 py-2 text-sm resize-y min-h-[48px]"
                  placeholder="Tények, számok — az AI szépen megfogalmazza…"
                />
              </label>
            ))}
          {!suggestLoading && unansweredSuggestions.length === 0 && suggested.length > 0 && (
            <p className="text-[10px] text-emerald-400/90 px-1">Minden javasolt kérdésre adtál választ ezen a listán.</p>
          )}
        </div>
      )}

      <label className="block text-xs text-slate-400">
        Egyéb megjegyzés az asszisztensnek
        <textarea
          data-testid={`${testidPrefix}-notes`}
          value={v.notes}
          onChange={(e) => set("notes", e.target.value)}
          rows={3}
          className="mt-1 w-full rounded-xl bg-white/[0.04] border border-white/10 px-3 py-2 text-sm resize-y min-h-[72px]"
          placeholder="Pl. téli gumi ajándék, csak hétvégén hívható…"
        />
      </label>
    </div>
  );
}
